import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Label } from '@/components/ui/label'
import { Input, InputPassword } from '@/components/ui/input'
import { EnhancedDatePicker } from '@/components/ui/enhanced-date-picker'
import { SelectHorizon, SelectPriority, SelectStatus } from '@/components/ui/select'
import { InputName } from '@/components/ui/input'
import { TextareaDescription } from '@/components/ui/textarea'
import { FormLayout } from '@/components/layout'
import { FormField, SelectField } from '@/components/forms'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { NodeImage } from '@/components/nodes/NodeImage'
import { NodeRelationshipSelection } from '@/components/nodes/NodeRelationshipSelection'
import { Boxes, CheckSquare, ClipboardList, Users, User as UserIcon } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useNodeFormData } from '@/hooks/useNodeFormData'
import { useNodeFormValidation } from '@/hooks/useNodeFormValidation'
import { Status, Priority, Horizon, TaskType } from '@/types/enums'
import type { Workspace, Project, User, Task } from '@/types'

// Helper functions for consistent NodeRelationshipSelection usage
const createNodeRenderer = {
  workspace: () => <Boxes className="h-4 w-4 text-muted-foreground" />,
  project: () => <ClipboardList className="h-4 w-4 text-muted-foreground" />,
  task: () => <CheckSquare className="h-4 w-4 text-muted-foreground" />,
  user: (user: User, size: 'xs' | 'sm' | 'md' = 'md') => <NodeAvatar node={user} size={size} />,
}

const createNodeDescription = {
  workspace: (workspace: Workspace) => workspace.email,
  project: (project: Project) => project.description,
  task: () => '',
  user: () => '',
}

const createPlaceholder = {
  workspace: 'Select a workspace',
  assignee: 'Select a user to assign',
  project: 'Associate with a project',
  collaborator: 'Add users as collaborators',
  dependency: 'Add tasks as dependencies',
}

const createLabel = {
  workspace: (required: boolean) => (required ? 'Workspace *' : 'Workspace'),
  assignee: (customLabel?: string) => customLabel || 'Assignee',
  project: (customLabel?: string) => customLabel || 'Projects',
  collaborator: (customLabel?: string) => customLabel || 'Collaborators',
  dependency: (customLabel?: string) => customLabel || 'Dependencies',
}

export interface NodeFormData {
  name: string
  description: string
  status: Status
  priority: Priority
  horizon: Horizon
  type: TaskType
  workspaceId: string
  assigneeId: string
  dueDate?: Date
  dependencyIds?: string[]
  email?: string
  logo?: string
  // Message-specific fields
  directMessage?: boolean
  userIds?: string[]
  projectIds?: string[]
  taskIds?: string[]
  documentIds?: string[]
  // Document-specific fields
  url?: string
  // User-specific fields
  location?: string
  role?: string
  timezone?: string
  userStatus?: string
  password?: string
  confirmPassword?: string
}

// Configuration for different node types
export interface NodeFormConfig {
  type: 'project' | 'task' | 'workspace' | 'message' | 'document' | 'user'
  nameLabel: string
  descriptionLabel: string
  descriptionRequired: boolean
  showType?: boolean
  showDueDate?: boolean
  showAssignee?: boolean
  showProjects?: boolean
  showDependencies?: boolean
  showWorkspace?: boolean
  showCollaborators?: boolean
  showLogo?: boolean
  showEmail?: boolean
  showHorizon?: boolean
  showPriority?: boolean
  showStatus?: boolean
  workspaceRequired?: boolean
  assigneeLabel?: string
  collaboratorsLabel?: string
  projectsLabel?: string
  dependenciesLabel?: string
  // Message-specific features
  showDirectMessage?: boolean
  showFromField?: boolean
  showRecipients?: boolean
  recipientsLabel?: string
  fromLabel?: string
  directMessageLabel?: string
  // Document-specific features
  showUrl?: boolean
  // User-specific features
  showLocation?: boolean
  showRole?: boolean
  showTimezone?: boolean
  showUserStatus?: boolean
  showPassword?: boolean
  showConfirmPassword?: boolean
  roleLabel?: string
  timezoneLabel?: string
  userStatusLabel?: string
  locationLabel?: string
  passwordLabel?: string
  confirmPasswordLabel?: string
}

interface NodeFormProps {
  mode: 'create' | 'edit'
  config: NodeFormConfig
  initialData?: Record<string, unknown>
  initialProjects?: Project[]
  initialDependencies?: Task[]
  initialUsers?: User[]
  excludeTaskId?: string // For filtering out current task from dependencies in edit mode
  onSubmit: (data: NodeFormData & Record<string, unknown>) => Promise<void>
  onCancel: () => void
  onDirtyChange?: (isDirty: boolean) => void
  loading?: boolean
  submitText?: string
  cancelText?: string
  title?: string
  description?: string
  simplified?: boolean // Show only name and description for onboarding
  // Workspace-specific props
  logo?: string
  onLogoChange?: (logo: string | undefined) => void
  currentUsers?: User[]
  usersToAdd?: User[]
  usersToRemove?: string[]
  availableUsers?: User[]
  onAddUser?: (userId: string) => void
  onRemoveUserToAdd?: (userId: string) => void
  onRemoveCurrentUser?: (userId: string) => void
  // Message-specific props
  primaryActionLoading?: boolean
  secondaryActionLoading?: boolean
  customActions?: React.ReactNode
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  // External data management props
  externalWorkspaces?: Workspace[]
  externalProjects?: Project[]
  externalTasks?: Task[]
  externalUsers?: User[]
  externalSelectedProjects?: Project[]
  externalSelectedDependencies?: Task[]
  externalSelectedUsers?: User[]
  onExternalWorkspaceChange?: (workspaceId: string) => void
  onExternalProjectAdd?: (projectId: string) => void
  onExternalProjectRemove?: (projectId: string) => void
  onExternalTaskAdd?: (taskId: string) => void
  onExternalTaskRemove?: (taskId: string) => void
  onExternalUserAdd?: (userId: string) => void
  onExternalUserRemove?: (userId: string) => void
}

export function NodeForm({
  mode,
  config,
  initialData,
  initialProjects = [],
  initialDependencies = [],
  initialUsers = [],
  excludeTaskId,
  onSubmit,
  onCancel,
  onDirtyChange,
  loading = false,
  submitText,
  cancelText,
  title,
  description: formDescription,
  simplified = false,
  // Workspace-specific props
  logo,
  onLogoChange,
  currentUsers = [],
  usersToAdd = [],
  usersToRemove = [],
  availableUsers = [],
  onAddUser,
  onRemoveUserToAdd,
  onRemoveCurrentUser,
  // Message-specific props
  primaryActionLoading: _primaryActionLoading = false,
  secondaryActionLoading: _secondaryActionLoading = false,
  customActions,
  onPrimaryAction,
  onSecondaryAction: _onSecondaryAction,
  // External data management props
  externalWorkspaces,
  externalProjects,
  externalTasks,
  externalUsers,
  externalSelectedProjects,
  externalSelectedDependencies,
  externalSelectedUsers,
  onExternalWorkspaceChange,
  onExternalProjectAdd,
  onExternalProjectRemove,
  onExternalTaskAdd,
  onExternalTaskRemove,
  onExternalUserAdd,
  onExternalUserRemove,
}: NodeFormProps) {
  const { user } = useAuth()

  // Use the data loading hook
  const {
    selectedDependencies,
    selectedUsers,
    setSelectedProjects,
    setSelectedDependencies,
    setSelectedUsers,
    effectiveWorkspaces,
    effectiveProjects,
    effectiveUsers,
    effectiveTasks,
    effectiveSelectedProjects,
    effectiveSelectedDependencies,
    effectiveSelectedUsers,
    loadProjectsForWorkspace,
    loadUsersForWorkspace,
  } = useNodeFormData({
    config,
    mode,
    excludeTaskId,
    externalWorkspaces,
    externalProjects,
    externalTasks,
    externalUsers,
    externalSelectedProjects,
    externalSelectedDependencies,
    externalSelectedUsers,
    initialProjects,
    initialDependencies,
    initialUsers,
  })

  // Use the validation hook
  const { getFieldValidation } = useNodeFormValidation({
    config,
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<NodeFormData>({
    defaultValues: {
      status: Status.ToDo,
      priority: Priority.None,
      horizon: Horizon.None,
      type: TaskType.Task,
      name: '',
      description: '',
      workspaceId: '',
      assigneeId: '',
      dueDate: undefined,
      email: '',
      logo: '',
      ...initialData,
    },
  })

  const selectedWorkspaceId = watch('workspaceId') as string
  const selectedType = watch('type') as TaskType
  const selectedStatus = watch('status') as Status
  const selectedPriority = watch('priority') as Priority
  const selectedHorizon = watch('horizon') as Horizon
  const selectedAssigneeId = watch('assigneeId') as string

  // Calculate if form is dirty (used for disabling submit button)
  const isFormDirty = useMemo(() => {
    const hasFormChanges = mode === 'edit' ? isDirty : Object.keys(dirtyFields).length > 0

    // Helper to check if two arrays of nodes have different IDs
    const arraysDiffer = <T extends { id: string }>(current: T[], initial: T[]): boolean => {
      if (current.length !== initial.length) return true
      const currentIds = new Set(current.map((item) => item.id))
      const initialIds = new Set(initial.map((item) => item.id))
      if (currentIds.size !== initialIds.size) return true
      for (const id of currentIds) {
        if (!initialIds.has(id)) return true
      }
      return false
    }

    const hasAssociationChanges =
      arraysDiffer(effectiveSelectedProjects, initialProjects) ||
      arraysDiffer(effectiveSelectedDependencies, initialDependencies) ||
      arraysDiffer(effectiveSelectedUsers, initialUsers) ||
      selectedWorkspaceId !== (initialData?.workspaceId || '')

    // Workspace-specific dirty state tracking
    const hasWorkspaceChanges =
      config.type === 'workspace' &&
      (logo !== initialData?.logo || usersToAdd.length > 0 || usersToRemove.length > 0)

    return hasFormChanges || hasAssociationChanges || hasWorkspaceChanges
  }, [
    isDirty,
    dirtyFields,
    mode,
    effectiveSelectedProjects,
    effectiveSelectedDependencies,
    effectiveSelectedUsers,
    initialProjects,
    initialDependencies,
    initialUsers,
    selectedWorkspaceId,
    initialData?.workspaceId,
    initialData?.logo,
    logo,
    usersToAdd.length,
    usersToRemove.length,
    config.type,
  ])

  // Track dirty state and notify parent
  useEffect(() => {
    if (!onDirtyChange) return
    onDirtyChange(isFormDirty)
  }, [isFormDirty, onDirtyChange])

  // Load workspace-specific data when workspace changes
  useEffect(() => {
    if (selectedWorkspaceId && !externalProjects && !externalUsers) {
      loadProjectsForWorkspace(selectedWorkspaceId)
      loadUsersForWorkspace(selectedWorkspaceId)
    }
  }, [
    selectedWorkspaceId,
    externalProjects,
    externalUsers,
    loadProjectsForWorkspace,
    loadUsersForWorkspace,
  ])

  const handleAddDependency = (task: Task) => {
    if (onExternalTaskAdd) {
      onExternalTaskAdd(task.id)
    } else if (!selectedDependencies.some((t) => t.id === task.id)) {
      setSelectedDependencies([...selectedDependencies, task])
    }
  }

  const handleRemoveDependency = (taskId: string) => {
    if (onExternalTaskRemove) {
      onExternalTaskRemove(taskId)
    } else {
      setSelectedDependencies(selectedDependencies.filter((t) => t.id !== taskId))
    }
  }

  const handleAddUser = (user: User) => {
    if (onExternalUserAdd) {
      onExternalUserAdd(user.id)
    } else if (!selectedUsers.some((u) => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
    }
  }

  const handleRemoveUser = (userId: string) => {
    if (onExternalUserRemove) {
      onExternalUserRemove(userId)
    } else {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== userId))
    }
  }

  const handleFormSubmit = async (data: NodeFormData) => {
    // In edit mode, prevent submission if form is not dirty
    if (mode === 'edit' && !isFormDirty) {
      return
    }

    // For workspace forms, we need to add the workspace-specific data
    if (config.type === 'workspace') {
      const workspaceData = {
        ...data,
        logo,
        userIds: [...currentUsers, ...usersToAdd].filter((u) => u).map((u) => u.id),
        usersToRemove,
      }
      await onSubmit(workspaceData)
    }
    // For task forms, we need to add the association data
    else if (config.type === 'task') {
      const taskData = {
        ...data,
        projectIds: effectiveSelectedProjects.filter((p) => p).map((p) => p.id),
        dependencyIds: effectiveSelectedDependencies.filter((t) => t).map((t) => t.id),
        userIds: effectiveSelectedUsers.filter((u) => u).map((u) => u.id),
      }
      await onSubmit(taskData)
    }
    // For message forms, we need to add the message-specific data
    else if (config.type === 'message') {
      const messageData = {
        ...data,
        userIds: effectiveSelectedUsers.filter((u) => u).map((u) => u.id),
        projectIds: effectiveSelectedProjects.filter((p) => p).map((p) => p.id),
        taskIds: effectiveSelectedDependencies.filter((t) => t).map((t) => t.id),
        documentIds: [], // TODO: Add document support when implemented
      }
      await onSubmit(messageData)
    }
    // For document forms, we need to add the document-specific data
    else if (config.type === 'document') {
      const documentData = {
        ...data,
        userIds: effectiveSelectedUsers.filter((u) => u).map((u) => u.id),
        projectIds: effectiveSelectedProjects.filter((p) => p).map((p) => p.id),
        taskIds: effectiveSelectedDependencies.filter((t) => t).map((t) => t.id),
      }
      await onSubmit(documentData)
    }
    // For user forms, we need to add the user-specific data
    else if (config.type === 'user') {
      const userData = {
        ...data,
        workspaceIds: effectiveSelectedProjects.filter((p) => p).map((p) => p.id), // Using projects as workspaceIds for user creation
        projectIds: effectiveSelectedProjects.filter((p) => p).map((p) => p.id),
      }
      await onSubmit(userData)
    } else {
      // For project forms, we only need userIds
      const projectData = {
        ...data,
        userIds: effectiveSelectedUsers.filter((u) => u).map((u) => u.id),
      }
      await onSubmit(projectData)
    }
  }

  return (
    <FormLayout
      title={title || ''}
      description={formDescription}
      onSubmit={onPrimaryAction || handleSubmit(handleFormSubmit)}
      onCancel={onCancel}
      submitText={
        submitText || (mode === 'create' ? `Create ${config.type}` : `Update ${config.type}`)
      }
      cancelText={cancelText}
      loading={loading}
      submitDisabled={mode === 'edit' && !isFormDirty}
      customActions={customActions}
    >
      <FormField
        label={config.nameLabel}
        required={config.nameLabel.includes('*')}
        error={errors.name?.message}
        htmlFor="name"
      >
        <InputName
          id="name"
          {...register('name', getFieldValidation('name'))}
          placeholder={`Enter ${config.type} name`}
          error={errors.name?.message}
        />
      </FormField>

      <FormField
        label={config.descriptionLabel}
        required={config.descriptionRequired}
        error={errors.description?.message}
        htmlFor="description"
      >
        <TextareaDescription
          id="description"
          placeholder={`Enter ${config.type} description`}
          register={register('description', getFieldValidation('description'))}
          error={errors.description?.message}
        />
      </FormField>

      {config.showLogo && (
        <FormField label="Logo">
          <NodeImage
            node={{ name: watch('name') || 'New Workspace', logo } as Workspace}
            size="xxl"
            onImageChange={onLogoChange}
            disabled={loading}
            uploadText="Upload Logo"
          />
        </FormField>
      )}

      {config.showEmail && (
        <FormField
          label="Email"
          required={true}
          error={errors.email?.message}
          helpText="This email will be used for workspace communications"
          htmlFor="email"
        >
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            {...register('email', getFieldValidation('email'))}
            aria-invalid={errors.email ? 'true' : 'false'}
            disabled={loading}
          />
        </FormField>
      )}

      {config.showUrl && (
        <FormField label="Document URL" error={errors.url?.message} htmlFor="url">
          <Input
            id="url"
            type="url"
            placeholder="https://example.com/document.pdf"
            {...register('url', getFieldValidation('url'))}
            disabled={loading}
          />
        </FormField>
      )}

      {config.showLocation && (
        <FormField
          label={config.locationLabel || 'Location'}
          error={errors.location?.message}
          htmlFor="location"
        >
          <Input
            id="location"
            placeholder="City, State"
            {...register('location')}
            disabled={loading}
          />
        </FormField>
      )}

      {config.showRole && (
        <SelectField
          name="role"
          label={config.roleLabel || 'Role'}
          value={watch('role') || ''}
          onValueChange={(value) => setValue('role', value)}
          options={[
            { value: 'SuperAdmin', label: 'Super Admin' },
            { value: 'Admin', label: 'Admin' },
            { value: 'User', label: 'User' },
          ]}
          error={errors.role?.message}
          disabled={loading}
        />
      )}

      {config.showTimezone && (
        <SelectField
          name="timezone"
          label={config.timezoneLabel || 'Timezone'}
          value={watch('timezone') || ''}
          onValueChange={(value) => setValue('timezone', value)}
          options={[
            { value: 'UTC', label: 'UTC' },
            { value: 'EST', label: 'EST' },
            { value: 'CST', label: 'CST' },
            { value: 'MST', label: 'MST' },
            { value: 'PST', label: 'PST' },
          ]}
          error={errors.timezone?.message}
          disabled={loading}
        />
      )}

      {config.showUserStatus && (
        <SelectField
          name="userStatus"
          label={config.userStatusLabel || 'Status'}
          value={watch('userStatus') || ''}
          onValueChange={(value) => setValue('userStatus', value)}
          options={[
            { value: 'Online', label: 'Online' },
            { value: 'Away', label: 'Away' },
            { value: 'Busy', label: 'Busy' },
            { value: 'Offline', label: 'Offline' },
          ]}
          error={errors.userStatus?.message}
          disabled={loading}
        />
      )}

      {config.showPassword && (
        <FormField
          label={config.passwordLabel || 'Password'}
          error={errors.password?.message}
          htmlFor="password"
        >
          <InputPassword
            id="password"
            placeholder="Enter password"
            {...register('password', getFieldValidation('password'))}
            disabled={loading}
            showStrength={true}
            showStrengthLabel={true}
            currentValue={watch('password')}
          />
        </FormField>
      )}

      {config.showConfirmPassword && (
        <FormField
          label={config.confirmPasswordLabel || 'Confirm Password'}
          error={errors.confirmPassword?.message}
          htmlFor="confirmPassword"
        >
          <InputPassword
            id="confirmPassword"
            placeholder="Confirm password"
            {...register('confirmPassword', getFieldValidation('confirmPassword'))}
            disabled={loading}
          />
        </FormField>
      )}

      {!simplified && config.showDirectMessage && (
        <SelectField
          name="directMessage"
          label={config.directMessageLabel || 'Message Type'}
          value={watch('directMessage') ? 'direct' : 'group'}
          onValueChange={(value) => {
            const isDirect = value === 'direct'
            setValue('directMessage', isDirect, { shouldDirty: true })
            // Reset recipients if switching to direct message
            if (isDirect && selectedUsers.length > 1) {
              setSelectedUsers([selectedUsers[0]])
            }
          }}
          options={[
            {
              value: 'direct',
              label: 'Direct Message',
              icon: <UserIcon className="h-4 w-4" />,
            },
            {
              value: 'group',
              label: 'Group Message',
              icon: <Users className="h-4 w-4" />,
            },
          ]}
          disabled={loading}
        />
      )}

      {!simplified && config.showType && (
        <SelectField
          name="type"
          label="Type"
          value={selectedType}
          onValueChange={(value) => setValue('type', value as TaskType, { shouldDirty: true })}
          options={[
            { value: TaskType.Task, label: 'Task' },
            { value: TaskType.Milestone, label: 'Milestone' },
            { value: TaskType.Approval, label: 'Approval' },
          ]}
          disabled={loading}
        />
      )}

      {!simplified && (config.showStatus || config.showPriority || config.showHorizon) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {config.showStatus && (
            <FormField label="Status" error={errors.status?.message}>
              <SelectStatus
                value={selectedStatus}
                onValueChange={(value) =>
                  setValue('status', value as Status, { shouldDirty: true })
                }
              />
            </FormField>
          )}

          {config.showPriority && (
            <FormField label="Priority" error={errors.priority?.message}>
              <SelectPriority
                value={selectedPriority}
                onValueChange={(value) =>
                  setValue('priority', value as Priority, { shouldDirty: true })
                }
              />
            </FormField>
          )}

          {config.showHorizon && (
            <FormField label="Horizon" error={errors.horizon?.message}>
              <SelectHorizon
                value={selectedHorizon}
                onValueChange={(value) =>
                  setValue('horizon', value as Horizon, { shouldDirty: true })
                }
              />
            </FormField>
          )}
        </div>
      )}

      {!simplified && config.showWorkspace && (
        <NodeRelationshipSelection<Workspace>
          availableNodes={effectiveWorkspaces}
          mode="single"
          selectedNode={
            selectedWorkspaceId
              ? effectiveWorkspaces.find((w) => w.id === selectedWorkspaceId) || null
              : null
          }
          onNodeChange={(workspace) => {
            setValue('workspaceId', workspace?.id || '', { shouldDirty: true })
            if (onExternalWorkspaceChange) {
              onExternalWorkspaceChange(workspace?.id || '')
            }
          }}
          loading={loading}
          label={createLabel.workspace(config.workspaceRequired || false)}
          placeholder={createPlaceholder.workspace}
          error={errors.workspaceId?.message}
          getNodeDescription={createNodeDescription.workspace}
          renderNode={createNodeRenderer.workspace}
        />
      )}

      {!simplified && config.showFromField && user && (
        <NodeRelationshipSelection<User>
          availableNodes={[user]}
          mode="single"
          selectedNode={user}
          onNodeChange={() => {}} // No-op since it's read-only
          label={config.fromLabel || 'From'}
          placeholder="Current user"
          disabled={true}
          renderNode={(user: User, size?: 'xs' | 'sm' | 'md') => (
            <NodeAvatar node={user} size={size || 'md'} />
          )}
        />
      )}

      {!simplified && config.showRecipients && (
        <NodeRelationshipSelection<User>
          availableNodes={effectiveUsers}
          mode="multi"
          selectedNodes={effectiveSelectedUsers}
          onAddNode={(user: User) => {
            if (watch('directMessage') && effectiveSelectedUsers.length >= 1) {
              // For direct messages, replace the existing recipient
              if (onExternalUserAdd) {
                onExternalUserAdd(user.id)
              } else {
                setSelectedUsers([user])
              }
            } else {
              // For group messages, just add the user
              if (onExternalUserAdd) {
                onExternalUserAdd(user.id)
              } else if (!effectiveSelectedUsers.some((u) => u.id === user.id)) {
                setSelectedUsers([...effectiveSelectedUsers, user])
              }
            }
          }}
          onRemoveNode={(userId: string) => {
            if (onExternalUserRemove) {
              onExternalUserRemove(userId)
            } else {
              setSelectedUsers(effectiveSelectedUsers.filter((u) => u.id !== userId))
            }
          }}
          maxSelections={watch('directMessage') ? 1 : undefined}
          loading={loading}
          label={config.recipientsLabel || 'Recipients'}
          placeholder="Add users as recipients"
          error={errors.userIds?.message}
          renderNode={(user: User, size?: 'xs' | 'sm' | 'md') => (
            <NodeAvatar node={user} size={size || 'md'} />
          )}
        />
      )}

      {!simplified && selectedWorkspaceId && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            {config.showAssignee && (
              <NodeRelationshipSelection<User>
                availableNodes={effectiveUsers}
                mode="single"
                selectedNode={
                  selectedAssigneeId
                    ? effectiveUsers.find((u) => u.id === selectedAssigneeId) || null
                    : null
                }
                onNodeChange={(user) =>
                  setValue('assigneeId', user?.id || '', { shouldDirty: true })
                }
                label={createLabel.assignee(config.assigneeLabel)}
                placeholder={createPlaceholder.assignee}
                error={errors.assigneeId?.message}
                currentUser={user}
                renderNode={createNodeRenderer.user}
              />
            )}

            {config.showDueDate && (
              <div className="flex flex-col space-y-3">
                <Label htmlFor="dueDate">Due Date</Label>
                <Controller
                  control={control}
                  name="dueDate"
                  render={({ field }) => (
                    <EnhancedDatePicker
                      value={field.value}
                      onChange={field.onChange}
                      minDate={new Date()}
                      mode={mode}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {config.showProjects && (
            <NodeRelationshipSelection<Project>
              availableNodes={effectiveProjects}
              mode="multi"
              selectedNodes={effectiveSelectedProjects}
              onAddNode={(project) => {
                if (onExternalProjectAdd) {
                  onExternalProjectAdd(project.id)
                } else if (!effectiveSelectedProjects.some((p) => p.id === project.id)) {
                  setSelectedProjects([...effectiveSelectedProjects, project])
                }
              }}
              onRemoveNode={(projectId) => {
                if (onExternalProjectRemove) {
                  onExternalProjectRemove(projectId)
                } else {
                  setSelectedProjects(effectiveSelectedProjects.filter((p) => p.id !== projectId))
                }
              }}
              label={createLabel.project(config.projectsLabel)}
              placeholder={createPlaceholder.project}
              getNodeDescription={createNodeDescription.project}
              renderNode={createNodeRenderer.project}
            />
          )}
        </>
      )}

      {!simplified && config.showCollaborators && (
        <NodeRelationshipSelection<User>
          availableNodes={
            config.type === 'workspace'
              ? availableUsers.filter(
                  (user) =>
                    !currentUsers.some((currentUser) => currentUser.id === user.id) &&
                    !usersToAdd.some((userToAdd) => userToAdd.id === user.id)
                )
              : effectiveUsers.filter((u) => u.id !== selectedAssigneeId)
          }
          mode="multi"
          selectedNodes={
            config.type === 'workspace' ? [...currentUsers, ...usersToAdd] : effectiveSelectedUsers
          }
          onAddNode={
            config.type === 'workspace'
              ? (user: User) => onAddUser?.(user.id)
              : onExternalUserAdd
                ? (user: User) => onExternalUserAdd(user.id)
                : handleAddUser
          }
          onRemoveNode={
            config.type === 'workspace'
              ? (userId: string) => {
                  // Check if it's a current user or a user to add
                  const currentUser = currentUsers.find((u) => u.id === userId)
                  if (currentUser && onRemoveCurrentUser) {
                    onRemoveCurrentUser(userId)
                  } else {
                    onRemoveUserToAdd?.(userId)
                  }
                }
              : onExternalUserRemove
                ? (userId: string) => onExternalUserRemove(userId)
                : handleRemoveUser
          }
          label={createLabel.collaborator(config.collaboratorsLabel)}
          placeholder={createPlaceholder.collaborator}
          loading={loading}
          disabled={loading}
          currentUser={user}
          renderNode={createNodeRenderer.user}
        />
      )}

      {!simplified && config.showDependencies && (
        <NodeRelationshipSelection<Task>
          availableNodes={effectiveTasks}
          mode="multi"
          selectedNodes={effectiveSelectedDependencies}
          onAddNode={handleAddDependency}
          onRemoveNode={handleRemoveDependency}
          label={createLabel.dependency(config.dependenciesLabel)}
          placeholder={createPlaceholder.dependency}
          error={errors.dependencyIds?.message}
          renderNode={createNodeRenderer.task}
        />
      )}
    </FormLayout>
  )
}
